import React, { useEffect, useMemo, useState } from 'react';
import { PeopleTeam48Regular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import type { SharedDashboardListItem } from '../api/types';
import { sharedDashboardsService } from '../api/sharedDashboardsService';
import CardListPage from '../components/shared/CardListPage';
import DashboardCard from '../components/dashboard-list/DashboardCard';

const SORT_OPTIONS = [
  { value: 'accepted', label: 'Recently Shared' },
  { value: 'updated', label: 'Last Updated' },
  { value: 'name', label: 'Name A-Z' },
];

const SharedDashboardListPage: React.FC = () => {
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState<SharedDashboardListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortValue, setSortValue] = useState('accepted');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    sharedDashboardsService.list()
      .then(({ dashboards: values }) => setDashboards(values))
      .catch(() => setErrorMessage('Shared dashboards could not be loaded. Try again.'))
      .finally(() => setIsLoading(false));
  }, []);

  const removeDashboard = async (dashboardId: string) => {
    setErrorMessage(null);
    try {
      await sharedDashboardsService.remove(dashboardId);
      setDashboards((current) => current.filter((dashboard) => dashboard.id !== dashboardId));
    } catch {
      setErrorMessage('The shared dashboard could not be removed. Try again.');
    }
  };

  const filtered = useMemo(() => dashboards
    .filter((item) => `${item.name} ${item.description ?? ''} ${item.ownerName}`.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortValue === 'name') return a.name.localeCompare(b.name);
      const field = sortValue === 'updated' ? 'updatedAt' : 'acceptedAt';
      return new Date(b[field]).getTime() - new Date(a[field]).getTime();
    }), [dashboards, searchQuery, sortValue]);

  return (
    <CardListPage
      title="Shared Dashboards"
      subtitle={`${dashboards.length} shared with you`}
      searchPlaceholder="Search shared dashboards..."
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      sortOptions={SORT_OPTIONS}
      sortValue={sortValue}
      onSortChange={setSortValue}
      isLoading={isLoading}
      isEmpty={dashboards.length === 0}
      filteredCount={filtered.length}
      errorMessage={errorMessage}
      layout="grid"
      emptyIcon={<PeopleTeam48Regular />}
      emptyTitle="No dashboards shared with you"
      emptyDescription="Dashboards you accept from others will appear here."
    >
      {filtered.map((item) => (
        <DashboardCard
          key={item.id}
          item={{
            ...item,
            createdAt: new Date(item.acceptedAt),
            updatedAt: new Date(item.updatedAt),
            sharedByName: item.ownerName,
          }}
          onClick={() => navigate(`/shared-dashboards/${item.id}`)}
          onDelete={removeDashboard}
          deleteLabel="Remove"
        />
      ))}
    </CardListPage>
  );
};

export default SharedDashboardListPage;